// Mock data for KoigonLabs.ai landing page

export const navigationLinks = [
  { label: 'Product', href: '#product' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Use Cases', href: '#use-cases' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Security', href: '#security' },
];

export const heroData = {
  headline: 'Replace resumes with proof-of-work.',
  subheadline: 'Short, job-relevant simulations with verified signals and Skill Maps—so you get a trusted shortlist, fast.',
  primaryCTA: 'Book a Demo',
  secondaryCTA: 'See a Simulation',
  microProofs: [
    '5–12 min simulations',
    'Verified identity',
    'Skill map for every candidate',
    'ATS-friendly',
  ],
};

export const problemData = {
  title: 'Traditional hiring is broken',
  problems: [
    { text: 'AI-generated resume spam', icon: 'FileWarning' },
    { text: 'Keyword filtering hides great talent', icon: 'Search' },
    { text: 'Signal-to-noise collapse', icon: 'TrendingDown' },
    { text: 'Trust in hiring is eroding', icon: 'ShieldOff' },
  ],
};

export const solutionData = {
  title: 'Proof-of-Work Hiring Infrastructure',
  subtitle: 'How Koigon fixes it',
  steps: [
    { title: 'Candidates complete real tasks', description: 'No more keyword-stuffed resumes. Real work, real signals.', icon: 'ClipboardCheck' },
    { title: 'Koigon generates Skill Maps', description: 'Visual breakdown of strengths, gaps, and potential.', icon: 'Map' },
    { title: 'Hiring teams get ranked, trusted shortlists', description: 'Data-driven decisions with verified candidates.', icon: 'ListChecks' },
  ],
};

export const featuresData = [
  {
    title: 'Job Simulations',
    description: 'Real-world tasks, not trivia',
    details: 'Candidates tackle role-specific challenges that mirror actual job responsibilities.',
    icon: 'Briefcase',
  },
  {
    title: 'Skill Maps',
    description: 'Strengths and gaps at a glance',
    details: 'Visual competency breakdown with actionable insights for hiring decisions.',
    icon: 'LayoutDashboard',
  },
  {
    title: 'Human Verification',
    description: 'Identity & behavior signals',
    details: 'Multi-layer verification ensures authentic candidates with genuine skills.',
    icon: 'UserCheck',
  },
];

export const howItWorksData = [
  { step: 1, title: 'Choose role simulation', description: 'Select from our library or create custom simulations', icon: 'MousePointerClick' },
  { step: 2, title: 'Candidates complete tasks', description: 'Real work samples in 5-12 minutes', icon: 'ClipboardList' },
  { step: 3, title: 'Get Skill Maps & shortlist', description: 'AI-powered analysis with human verification', icon: 'BarChart3' },
  { step: 4, title: 'Export to your ATS', description: 'Seamless integration with your workflow', icon: 'Upload' },
];

export const useCasesData = [
  { title: 'Cybersecurity', description: 'Test incident response, threat analysis, and security protocols.', icon: 'Shield' },
  { title: 'Customer Success', description: 'Evaluate communication skills, problem-solving, and empathy.', icon: 'HeartHandshake' },
  { title: 'Healthcare Ops', description: 'Assess clinical workflows, compliance knowledge, and patient care.', icon: 'Stethoscope' },
  { title: 'AI / Data', description: 'Validate technical skills, analytical thinking, and model comprehension.', icon: 'Brain' },
];

export const pricingData = [
  {
    tier: 'Starter',
    tagline: 'Validate signal quality',
    features: ['5 simulations/month', 'Basic Skill Maps', 'Email support', 'Standard verification'],
    cta: 'Talk to us',
    highlighted: false,
  },
  {
    tier: 'Growth',
    tagline: 'Custom simulations & integrations',
    features: ['50 simulations/month', 'Advanced Skill Maps', 'ATS integrations', 'Priority support', 'Custom branding'],
    cta: 'Book a Demo',
    highlighted: true,
  },
  {
    tier: 'Enterprise',
    tagline: 'SSO, audit trails, advanced verification',
    features: ['Unlimited simulations', 'Custom simulation builder', 'SSO & SCIM', 'Audit logs', 'Dedicated success manager', 'SLA guarantee'],
    cta: 'Talk to us',
    highlighted: false,
  },
];

export const securityData = {
  title: 'Enterprise-grade security',
  subtitle: 'Built for trust at every layer',
  features: [
    { title: 'Encryption in transit & at rest', icon: 'Lock' },
    { title: 'Role-based access control', icon: 'Users' },
    { title: 'Complete audit logs', icon: 'FileText' },
    { title: 'Privacy-first candidate handling', icon: 'Eye' },
  ],
};

export const finalCTAData = {
  headline: 'Ready to replace resumes with proof-of-work?',
  subtext: 'Book a demo and get a sample Skill Map for one of your open roles.',
  cta: 'Book a Demo',
};

export const footerData = {
  brand: 'KoigonLabs.ai',
  links: [
    { label: 'Privacy', href: '#privacy' },
    { label: 'Terms', href: '#terms' },
    { label: 'Contact', href: '#contact' },
  ],
  copyright: `© ${new Date().getFullYear()} KoigonLabs.ai. All rights reserved.`,
};
